/**
 * AURELIA — component-drawer.js
 * The drawer base class every overlay in the theme extends: menu, cart,
 * search, filters and quick view.
 *
 * Responsibilities, in one place so they are impossible to get wrong twice:
 *   • open/close with a real transition, not a display toggle
 *   • focus trap on open, focus restored to the trigger on close
 *   • scroll lock without layout shift
 *   • Escape and overlay click to dismiss
 *   • aria-expanded kept in sync on every trigger that points at the drawer
 *   • only one drawer open at a time
 *
 * Exposed as `Aurelia.Drawer` for other component files to extend.
 */
(function () {
  'use strict';

  const { Element, a11y, emit, on, motion } = window.Aurelia;

  /** The drawer currently open, so opening a second one closes the first. */
  let activeDrawer = null;

  class Drawer extends Element {
    /**
     * Looked up on every access rather than cached in mount(). A drawer may
     * replace its own contents while it lives — the cart does, on every cart
     * update — and a cached node is left detached by that swap. A detached
     * panel takes no focus and reports zero size, so the focus trap silently
     * traps nothing and the closing transition never resolves.
     */
    get panel() {
      return this.querySelector('.drawer__panel');
    }

    get overlay() {
      return this.querySelector('[data-drawer-overlay]');
    }

    mount() {
      this.isOpen = false;

      // Close controls are matched on the way up from the click target rather
      // than bound to each node, for the same reason: listeners bound to the
      // buttons directly would be discarded by a re-render.
      this.listen(this, 'click', (event) => {
        if (event.target.closest('[data-drawer-close], [data-drawer-overlay]')) {
          this.close();
        }
      });

      this.subscribe('escape', () => {
        if (this.isOpen) this.close();
      });

      // Any element pointing at this drawer with aria-controls becomes a
      // trigger — sections do not need to register anything.
      this.triggers = Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
      this.triggers.forEach((trigger) => {
        this.listen(trigger, 'click', (event) => {
          event.preventDefault();
          this.toggle(trigger);
        });
      });

      if (typeof this.setup === 'function') this.setup();
    }

    unmount() {
      if (this.isOpen) this.close({ restoreFocus: false });
    }

    toggle(trigger) {
      if (this.isOpen) this.close();
      else this.open(trigger);
    }

    open(trigger) {
      if (this.isOpen) return;

      if (activeDrawer && activeDrawer !== this) activeDrawer.close({ restoreFocus: false });
      activeDrawer = this;

      this.opener = trigger || document.activeElement;
      this.hidden = false;

      // Force a reflow so the browser registers the pre-transition state and
      // actually animates rather than snapping to the open position.
      void this.offsetHeight;

      this.classList.add('is-open');
      this.isOpen = true;

      a11y.lockScroll();
      this.syncTriggers(true);

      const focusTarget = this.querySelector('[data-drawer-focus]') || this.panel;
      a11y.trapFocus(this.panel, focusTarget === this.panel ? undefined : focusTarget);

      if (typeof this.onOpen === 'function') this.onOpen();
      emit('drawer:opened', { id: this.id, drawer: this });
    }

    async close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;

      this.classList.remove('is-open');
      this.isOpen = false;
      if (activeDrawer === this) activeDrawer = null;

      a11y.unlockScroll();

      // Focus goes back to whatever opened the drawer. The focus trap only
      // remembers the element that was active when it was set, which is not
      // the opener when a drawer is opened asynchronously — an add to cart
      // disables its own submit button while the request is in flight, so
      // focus has already fallen to <body> by the time the drawer opens.
      const opener = this.opener;
      const restoreToOpener =
        restoreFocus && opener && opener.isConnected && !opener.disabled && typeof opener.focus === 'function';

      a11y.releaseFocus(restoreFocus && !restoreToOpener);
      if (restoreToOpener) opener.focus({ preventScroll: true });

      this.syncTriggers(false);

      if (typeof this.onClose === 'function') this.onClose();
      emit('drawer:closed', { id: this.id, drawer: this });

      // Wait for the slide-out before removing the element from the layout,
      // otherwise the panel disappears instantly on close.
      await motion.afterTransition(this.panel || this);
      if (!this.isOpen) this.hidden = true;
    }

    syncTriggers(expanded) {
      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', String(expanded));
      });
    }
  }

  window.Aurelia.Drawer = Drawer;

  // Requests from anywhere in the theme, e.g. product-form asking for the cart.
  on('drawer:open-request', ({ id }) => {
    const drawer = document.getElementById(id);
    if (drawer && typeof drawer.open === 'function') drawer.open();
  });
})();
